import { z } from "zod";
import {
  IntellioAssistantArtifactRefSchema,
  IntellioAssistantRuntimeAccessControlSchema,
  IntellioAssistantModuleSchema,
  IntellioAuthorityContextSchema,
  IntellioOnBehalfWorkflowContextSchema,
  IntellioRoutingHintsSchema,
} from "../entities/assistant";
import { AssistantActiveRouteContextEnvelopeSchema } from "./assistant-active-route-contract";
import type { DomainRouterJsonInput } from "../entities/domain-router-transport";
import type {
  AppSyncJsonArgument,
  IntellioAssistantReportGenerationWorkspaceArguments,
  IntellioAssistantReportGenerationWorkspaceOutput,
  IntellioAppendAssistantConversationMessagesArguments,
  IntellioAssistantReportPreviewArguments,
  IntellioAssistantReportPreviewOutput,
  IntellioAssistantReportScheduleConnection,
  IntellioAssistantReportScheduleListArguments,
  IntellioAssistantReportTemplateConnection,
  IntellioAssistantReportTemplateListArguments,
  IntellioAssistantConversationSessionConnection,
  IntellioAssistantConversationSessionMutationResult,
  IntellioAssistantConversationSessionQueryArguments,
  IntellioAssistantConversationTimelineArguments,
  IntellioAssistantConversationTimelineOutput,
  IntellioAssistantWorkflowConnection,
  IntellioAssistantWorkflowExecutionConnection,
  IntellioAssistantWorkflowExecutionGetArguments,
  IntellioAssistantWorkflowExecutionListArguments,
  IntellioAssistantWorkflowExecutionRecord,
  IntellioAssistantWorkflowGetArguments,
  IntellioAssistantWorkflowMutationResult,
  IntellioAssistantWorkflowStudioStateArguments,
  IntellioAssistantWorkflowStudioStateOutput,
  IntellioAssistantWorkflowTemplateConnection,
  IntellioAssistantWorkflowTemplateQueryArguments,
  IntellioAssistantSessionStateArguments,
  IntellioAssistantSessionStateOutput,
  IntellioCloseAssistantConversationSessionArguments,
  IntellioAssistantWorkItemsQueryArguments,
  IntellioAssistantWorkItemsQueryOutput,
  IntellioAssistantWorkflowListArguments,
  IntellioMutateAssistantReportScheduleArguments,
  IntellioMutateAssistantReportScheduleOutput,
  IntellioMutateAssistantReportTemplateArguments,
  IntellioMutateAssistantReportTemplateOutput,
  IntellioMutateAssistantRunArguments,
  IntellioMutateAssistantRunOutput,
  IntellioMutateAssistantWorkItemsArguments,
  IntellioMutateAssistantWorkItemsOutput,
  IntellioReviewAssistantWorkflowArguments,
  IntellioStartAssistantConversationSessionArguments,
  IntellioSubmitAssistantWorkflowForApprovalArguments,
  IntellioUpsertAssistantWorkflowDraftArguments,
} from "./assistant-platform-contract";

export const AssistantCapabilityTypeSchema = z.enum(["insight", "action"]);
export type AssistantCapabilityType = z.infer<
  typeof AssistantCapabilityTypeSchema
>;

export const AssistantGenerationModeSchema = z.enum([
  "deterministic_only",
  "deterministic_with_llm_rewrite",
  "llm_with_deterministic_guard",
  "llm_only",
  "task",
]);
export type AssistantGenerationMode = z.infer<
  typeof AssistantGenerationModeSchema
>;

export const AssistantGenerationFallbackSchema = z.enum([
  "deterministic",
  "error",
  "none",
]);
export type AssistantGenerationFallback = z.infer<
  typeof AssistantGenerationFallbackSchema
>;

export const AssistantGenerationRewriteContextStrategySchema = z.enum([
  "deterministic_locked_fact_brief",
]);
export type AssistantGenerationRewriteContextStrategy = z.infer<
  typeof AssistantGenerationRewriteContextStrategySchema
>;

export const AssistantGenerationRewriteToneSchema = z.enum([
  "warm_concise",
  "neutral_concise",
]);
export type AssistantGenerationRewriteTone = z.infer<
  typeof AssistantGenerationRewriteToneSchema
>;

export const AssistantGenerationRewritePolicySchema = z
  .object({
    enabled: z.boolean().default(true),
    contextStrategy: AssistantGenerationRewriteContextStrategySchema.default(
      "deterministic_locked_fact_brief",
    ),
    responseMode: z.string().trim().min(1).default("deterministic-prose-rewrite"),
    expectedAnswerStyle: z.string().trim().min(1).default("rewrite-answer-and-summary-only"),
    intentHint: z.string().trim().min(1).default("deterministic-prose-rewrite"),
    tone: AssistantGenerationRewriteToneSchema.default("warm_concise"),
    allowedRewriteTargets: z
      .array(z.string().trim().min(1))
      .min(1)
      .default([
        "answer",
        "presentation.sections[type=summary].text",
      ]),
    maxLockedFacts: z.number().int().positive().max(200).default(80),
    preserveDeterministicFacts: z.boolean().default(true),
  })
  .strict();
export type AssistantGenerationRewritePolicy = z.infer<
  typeof AssistantGenerationRewritePolicySchema
>;

export const AssistantGenerationPolicySchema = z
  .object({
    mode: AssistantGenerationModeSchema,
    llmRole: z.string().trim().min(1).nullable().optional(),
    deterministicHandlerId: z.string().trim().min(1).nullable().optional(),
    maxRewriteLatencyMs: z.number().int().positive().max(30_000).nullable().optional(),
    fallback: AssistantGenerationFallbackSchema.default("deterministic"),
    rewrite: AssistantGenerationRewritePolicySchema.nullable().optional(),
  })
  .strict();
export type AssistantGenerationPolicy = z.infer<
  typeof AssistantGenerationPolicySchema
>;

export const AssistantCapabilitySchema = z
  .object({
    id: z.string(),
    page: z.string(),
    tab: z.string(),
    sequence: z.number().int(),
    title: z.string(),
    type: AssistantCapabilityTypeSchema,
    module: IntellioAssistantModuleSchema,
    prompt: z.string(),
    rationale: z.string(),
    method: z.string().nullable(),
    requiresConfirmation: z.boolean(),
    actionKey: z.string().nullable(),
    targetRoute: z.string().nullable(),
    sourceOrigin: z.string().nullable(),
    scenarioKey: z.string().nullable(),
    likelySources: z.array(z.string()),
    routePrefixes: z.array(z.string()),
    dataStateSignals: z.array(z.string()),
    requiredFacets: z.array(z.string().trim().min(1)).default([]),
    requiredArtifactTypes: z.array(z.string().trim().min(1)).default([]),
    requiresFreshRouteContext: z.boolean().default(false),
    generationPolicy: AssistantGenerationPolicySchema.optional(),
  })
  .strict();
export type AssistantCapability = z.infer<typeof AssistantCapabilitySchema>;

export const AssistantCapabilityConnectionSchema = z
  .object({
    items: z.array(AssistantCapabilitySchema),
    totalCount: z.number().int().nonnegative(),
    policyEnabled: z.boolean(),
    policyReason: z.string(),
  })
  .strict();
export type AssistantCapabilityConnection = z.infer<
  typeof AssistantCapabilityConnectionSchema
>;

export const AssistantToolKindSchema = z.enum([
  "provider.native",
  "provider.bedrock",
  "custom.augmented",
  "custom.codified",
  "custom.interpreter",
]);
export type AssistantToolKind = z.infer<typeof AssistantToolKindSchema>;

export const AssistantToolExecutionPlaneSchema = z.enum([
  "supervisor_query",
  "ui_action",
  "async_task",
]);
export type AssistantToolExecutionPlane = z.infer<
  typeof AssistantToolExecutionPlaneSchema
>;

export const AssistantToolVisibilitySchema = z.enum(["visible", "hidden"]);
export type AssistantToolVisibility = z.infer<
  typeof AssistantToolVisibilitySchema
>;

export const AssistantToolPromotionSchema = z.enum([
  "promoted",
  "available",
  "hidden",
]);
export type AssistantToolPromotion = z.infer<
  typeof AssistantToolPromotionSchema
>;

export const AssistantToolGroupLabelSchema = z.enum([
  "Insights",
  "Actions",
  "Custom Tools",
  "Internal",
]);
export type AssistantToolGroupLabel = z.infer<
  typeof AssistantToolGroupLabelSchema
>;

export const AssistantToolSchema = z
  .object({
    toolId: z.string(),
    title: z.string(),
    summary: z.string(),
    toolKind: AssistantToolKindSchema,
    executionPlane: AssistantToolExecutionPlaneSchema,
    module: IntellioAssistantModuleSchema,
    routePrefixes: z.array(z.string()),
    personas: z.array(z.string()),
    visibility: AssistantToolVisibilitySchema,
    promotion: AssistantToolPromotionSchema,
    prompt: z.string().nullable(),
    requiresConfirmation: z.boolean(),
    executorRef: z.string().nullable(),
    providerToolName: z.string().nullable(),
    capabilityId: z.string().nullable(),
    capabilityType: AssistantCapabilityTypeSchema.nullable(),
    actionKey: z.string().nullable(),
    workflowKey: z.string().nullable(),
    taskClass: z
      .enum([
        "artifact_generate",
        "compute_report",
        "draft_then_approve",
        "external_side_effect",
      ])
      .nullable(),
    page: z.string().nullable(),
    tab: z.string().nullable(),
    sequence: z.number().int().nullable(),
    method: z.string().nullable(),
    targetRoute: z.string().nullable(),
    sourceOrigin: z.string().nullable(),
    scenarioKey: z.string().nullable(),
    likelySources: z.array(z.string()),
    dataStateSignals: z.array(z.string()),
    groupLabel: AssistantToolGroupLabelSchema,
    supportsProactivePromotion: z.boolean(),
    userVisible: z.boolean(),
  })
  .strict();
export type AssistantTool = z.infer<typeof AssistantToolSchema>;

export const AssistantToolConnectionSchema = z
  .object({
    items: z.array(AssistantToolSchema),
    totalCount: z.number().int().nonnegative(),
    policyEnabled: z.boolean(),
    policyReason: z.string(),
  })
  .strict();
export type AssistantToolConnection = z.infer<
  typeof AssistantToolConnectionSchema
>;

export const AssistantActionPreviewResponseSchema = z
  .object({
    capabilityId: z.string(),
    allowed: z.boolean(),
    requiresConfirmation: z.boolean(),
    confirmationText: z.string().nullable(),
    summary: z.string(),
    message: z.string(),
    prompt: z.string(),
    actionKey: z.string().nullable(),
    targetRoute: z.string().nullable(),
    requiredInputs: z.array(z.string()),
    missingInputs: z.array(z.string()).optional(),
  })
  .strict();
export type AssistantActionPreviewResponse = z.infer<
  typeof AssistantActionPreviewResponseSchema
>;

export const AssistantActionExecutionStatusSchema = z.enum([
  "completed",
  "needs_input",
  "blocked",
]);
export type AssistantActionExecutionStatus = z.infer<
  typeof AssistantActionExecutionStatusSchema
>;

export const AssistantActionExecutionResponseSchema = z
  .object({
    capabilityId: z.string(),
    status: AssistantActionExecutionStatusSchema,
    message: z.string(),
    actionKey: z.string().nullable(),
    mutationOperation: z.string().nullable(),
    navigateTo: z.string().nullable(),
    requiredInputs: z.array(z.string()),
    updatedAt: z.number().int().nullable(),
  })
  .strict();
export type AssistantActionExecutionResponse = z.infer<
  typeof AssistantActionExecutionResponseSchema
>;

export const AssistantCitationSchema = z
  .object({
    table: z.string(),
    key: z.union([z.record(z.string(), z.unknown()), z.string()]),
    fieldPath: z.string(),
    preview: z.string(),
    applicationCode: z.string().optional(),
    entityCode: z.string().optional(),
    module: IntellioAssistantModuleSchema.optional(),
  })
  .strict();
export type AssistantCitation = z.infer<typeof AssistantCitationSchema>;

export const AssistantResponseActionSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    type: z.enum(["followup", "navigate", "execute", "draft"]),
    kind: z.enum(["followup", "navigate", "execute", "draft"]).optional(),
    capabilityId: z.string().nullable().optional(),
    targetRoute: z.string().nullable().optional(),
    entityRef: z
      .object({
        kind: z.string(),
        id: z.string(),
        label: z.string().nullable().optional(),
        module: IntellioAssistantModuleSchema.optional(),
      })
      .nullable()
      .optional(),
    requiresConfirmation: z.boolean().optional(),
    approvalCategory: z.string().nullable().optional(),
    actionCategory: z.string().nullable().optional(),
    mutationOperation: z.string().nullable().optional(),
    missingInputs: z.array(z.string()).optional(),
    confidenceMode: z.enum(["full", "navigation_only"]).optional(),
    payload: z
      .object({
        prompt: z.string().optional(),
        href: z.string().optional(),
        targetRoute: z.string().optional(),
        capabilityId: z.string().optional(),
        requiresConfirmation: z.boolean().optional(),
        approvalCategory: z.string().optional(),
        actionCategory: z.string().optional(),
        mutationOperation: z.string().optional(),
        missingInputs: z.array(z.string()).optional(),
        confidenceMode: z.enum(["full", "navigation_only"]).optional(),
        routeContextRef: z.string().optional(),
        routeRevision: z.string().optional(),
        policyEventType: z.string().optional(),
        artifactActionId: z.string().optional(),
        entityId: z.string().optional(),
        entityLabel: z.string().optional(),
        entityKind: z.string().optional(),
      })
      .catchall(z.unknown()),
  })
  .strict();
export type AssistantResponseAction = z.infer<
  typeof AssistantResponseActionSchema
>;

export const AdmissionsAIResponseSchema = z
  .object({
    answer: z.string(),
    citations: z.array(AssistantCitationSchema),
    actions: z.array(AssistantResponseActionSchema).optional(),
    debug: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .strict();
export type AdmissionsAIResponse = z.infer<typeof AdmissionsAIResponseSchema>;

export const SchoolAIResponseSchema = z
  .object({
    answer: z.string(),
    citations: z.array(AssistantCitationSchema),
    actions: z.array(AssistantResponseActionSchema),
    module: IntellioAssistantModuleSchema,
    presentation: z.record(z.string(), z.unknown()).optional(),
    activeRouteContext: AssistantActiveRouteContextEnvelopeSchema.nullable().optional(),
    debug: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();
export type SchoolAIResponse = z.infer<typeof SchoolAIResponseSchema>;

export const AssistantLearningStatusSchema = z
  .object({
    status: z.enum(["collecting", "ready"]),
    updatedAt: z.string(),
    replayMatchRate: z.number().min(0).max(1),
  })
  .strict();
export type AssistantLearningStatus = z.infer<
  typeof AssistantLearningStatusSchema
>;

export const AssistantFeedbackArtifactStatusSchema = z.enum([
  "present",
  "missing",
  "mismatch",
]);
export type AssistantFeedbackArtifactStatus = z.infer<
  typeof AssistantFeedbackArtifactStatusSchema
>;

export const AssistantLearningStatusSourceSchema = z.enum([
  "episode",
  "route",
  "global",
  "unavailable",
]);
export type AssistantLearningStatusSource = z.infer<
  typeof AssistantLearningStatusSourceSchema
>;

export const AssistantRuntimePolicySourceSchema = z.enum([
  "s3-object",
  "artifact-file",
  "env-json",
  "embedded-default",
]);
export type AssistantRuntimePolicySource = z.infer<
  typeof AssistantRuntimePolicySourceSchema
>;

export const AssistantFeedbackAuthorityFeedbackSchema = z
  .object({
    feedbackId: z.string(),
    feedbackArtifactRef: z.string(),
    replayHash: z.string().nullable().optional(),
    artifactStatus: AssistantFeedbackArtifactStatusSchema,
    route: z.string().nullable().optional(),
    persona: z.string().nullable().optional(),
    projectionPlanHash: z.string().nullable().optional(),
    outcomeRef: z.string().nullable().optional(),
    submittedAt: z.string().nullable().optional(),
  })
  .strict();
export type AssistantFeedbackAuthorityFeedback = z.infer<
  typeof AssistantFeedbackAuthorityFeedbackSchema
>;

export const AssistantFeedbackAuthorityLearningSchema = z
  .object({
    status: z.enum(["collecting", "ready"]),
    artifactBacked: z.boolean(),
    statusSource: AssistantLearningStatusSourceSchema,
    updatedAt: z.string().nullable().optional(),
    artifactUpdatedAt: z.string().nullable().optional(),
    replayMatchRate: z.number().min(0).max(1).nullable().optional(),
  })
  .strict();
export type AssistantFeedbackAuthorityLearning = z.infer<
  typeof AssistantFeedbackAuthorityLearningSchema
>;

export const AssistantFeedbackAuthorityRuntimePolicySchema = z
  .object({
    policyId: z.string(),
    revision: z.number().int().nonnegative(),
    executionMode: z.enum(["shadow", "live"]),
    policySource: AssistantRuntimePolicySourceSchema,
    updatedAt: z.string().nullable().optional(),
  })
  .strict();
export type AssistantFeedbackAuthorityRuntimePolicy = z.infer<
  typeof AssistantFeedbackAuthorityRuntimePolicySchema
>;

export const AssistantFeedbackAuthorityLessonSignalSchema = z
  .object({
    lessonId: z.string(),
    verdict: z.enum(["PASS", "FAIL", "STALE", "MISSING", "ADVISORY_WARN"]),
    gateMode: z.enum(["advisory", "blocking"]),
    failureClass: z
      .enum([
        "execution-context",
        "policy-contract",
        "domain-readiness",
        "evidence-coherence",
        "security",
        "lineage",
      ])
      .nullable()
      .optional(),
    lifecycleStage: z.enum(["A", "B", "C"]).nullable().optional(),
    severity: z
      .enum(["low", "medium", "high", "critical"])
      .nullable()
      .optional(),
    sourcePath: z.string(),
  })
  .strict();
export type AssistantFeedbackAuthorityLessonSignal = z.infer<
  typeof AssistantFeedbackAuthorityLessonSignalSchema
>;

export const AssistantFeedbackAuthoritySourceRefSchema = z
  .object({
    kind: z.string(),
    ref: z.string(),
    present: z.boolean(),
  })
  .strict();
export type AssistantFeedbackAuthoritySourceRef = z.infer<
  typeof AssistantFeedbackAuthoritySourceRefSchema
>;

export const AssistantFeedbackAuthoritySchema = z
  .object({
    feedback: AssistantFeedbackAuthorityFeedbackSchema,
    learning: AssistantFeedbackAuthorityLearningSchema,
    runtimePolicy: AssistantFeedbackAuthorityRuntimePolicySchema,
    lessonSignals: z.array(AssistantFeedbackAuthorityLessonSignalSchema),
    sourceRefs: z.array(AssistantFeedbackAuthoritySourceRefSchema),
  })
  .strict();
export type AssistantFeedbackAuthority = z.infer<
  typeof AssistantFeedbackAuthoritySchema
>;

export const AssistantRuntimePolicySchema = z
  .object({
    policyId: z.string(),
    revision: z.number().int().nonnegative(),
    executionMode: z.enum(["shadow", "live"]),
    updatedAt: z.string(),
    policyJson: z.string(),
  })
  .strict();
export type AssistantRuntimePolicy = z.infer<
  typeof AssistantRuntimePolicySchema
>;

export const AssistantRuntimePolicyPatchSchema = z
  .object({
    executionMode: z.enum(["shadow", "live"]).optional(),
    reason: z.string().min(1).max(500).optional(),
    accessControl: IntellioAssistantRuntimeAccessControlSchema.optional(),
  })
  .strict();
export type AssistantRuntimePolicyPatch = z.infer<
  typeof AssistantRuntimePolicyPatchSchema
>;

export const AssistantRuntimePolicyAckSchema = z
  .object({
    accepted: z.boolean(),
    policyId: z.string(),
    revision: z.number().int().nonnegative(),
    executionMode: z.enum(["shadow", "live"]),
    updatedAt: z.string(),
    persistenceStore: z.enum(["s3-object", "artifact-file"]).optional(),
    persistenceTarget: z.string().optional(),
    mirrorPath: z.string().nullable().optional(),
    mirrorWritten: z.boolean().optional(),
  })
  .strict();
export type AssistantRuntimePolicyAck = z.infer<
  typeof AssistantRuntimePolicyAckSchema
>;

export const AssistantFeedbackInputSchema = z
  .object({
    assistantEpisodeId: z.string().min(1),
    assistantMessageId: z.string().min(1),
    route: z.string().min(1),
    routeMetadataToken: z.string().optional().nullable(),
    persona: z.string().min(1),
    feedback: z.enum(["up", "down"]),
    reasonCode: z.string().min(1),
    comment: z.string().max(1000).optional().nullable(),
    projectionPlanHash: z.string().min(1),
    outcomeRef: z.string().optional().nullable(),
    ts: z.string().optional().nullable(),
  })
  .strict();
export type AssistantFeedbackInput = z.infer<
  typeof AssistantFeedbackInputSchema
>;

export const AssistantFeedbackAckSchema = z
  .object({
    accepted: z.boolean(),
    feedbackId: z.string(),
    replayHash: z.string(),
    artifactRefs: z.array(z.string()),
  })
  .strict();
export type AssistantFeedbackAck = z.infer<typeof AssistantFeedbackAckSchema>;

export const AssistantAskContextSchema = z
  .object({
    module: z.union([IntellioAssistantModuleSchema, z.string()]).optional(),
    pathname: z.string().optional(),
    personaRole: z.string().optional(),
    personaName: z.string().optional(),
    selectedIds: z.array(z.string()).optional(),
    activeApplicationId: z.string().optional(),
    activeCapabilityId: z.string().optional(),
    activeScenarioKey: z.string().optional(),
    activeCapabilityMethod: z.string().optional(),
    authorityContext: IntellioAuthorityContextSchema.optional(),
    onBehalfOf: IntellioOnBehalfWorkflowContextSchema.optional(),
  })
  .catchall(z.unknown());
export type AssistantAskContext = z.infer<typeof AssistantAskContextSchema>;

export type AskAdmissionsAIArguments = {
  question: string;
  conversationHistory?: AppSyncJsonArgument<unknown[]>;
  context?: AppSyncJsonArgument<AssistantAskContext>;
  model?: string;
  runtimePreferences?: AppSyncJsonArgument<Record<string, unknown>>;
  routingHints?: AppSyncJsonArgument<
    z.infer<typeof IntellioRoutingHintsSchema>
  >;
};

export type AskSchoolAIArguments = {
  question: string;
  conversationHistory?: AppSyncJsonArgument<unknown[]>;
  context?: AppSyncJsonArgument<AssistantAskContext>;
  model?: string;
  runtimePreferences?: AppSyncJsonArgument<Record<string, unknown>>;
  routingHints?: AppSyncJsonArgument<
    z.infer<typeof IntellioRoutingHintsSchema>
  >;
};

export const ASSISTANT_PLATFORM_QUERY_OPERATION_IDS = [
  "listAssistantTools",
  "listAssistantCapabilities",
  "previewAssistantAction",
  "previewAssistantReportGeneration",
  "getAssistantReportGenerationWorkspace",
  "getAssistantFeedbackAuthority",
  "getAssistantLearningStatus",
  "getAssistantRuntimePolicy",
  "listAssistantWorkItems",
  "getAssistantSessionState",
  "listAssistantConversationSessions",
  "getAssistantConversationSessionTimeline",
  "getAssistantChartArtifactState",
  "getAssistantVisualization3DArtifactState",
  "listAssistantReportTemplates",
  "listAssistantReportSchedules",
  "listAssistantWorkflowTemplates",
  "listAssistantWorkflows",
  "getAssistantWorkflow",
  "getAssistantWorkflowStudioState",
  "listAssistantWorkflowExecutions",
  "getAssistantWorkflowExecution",
] as const;

export const ASSISTANT_PLATFORM_MUTATION_OPERATION_IDS = [
  "askAdmissionsAI",
  "askSchoolAI",
  "executeAssistantAction",
  "submitAssistantFeedback",
  "updateAssistantRuntimePolicy",
  "mutateAssistantWorkItems",
  "mutateAssistantRun",
  "mutateAssistantReportTemplate",
  "mutateAssistantReportSchedule",
  "startAssistantConversationSession",
  "closeAssistantConversationSession",
  "appendAssistantConversationMessages",
  "upsertAssistantWorkflowDraft",
  "submitAssistantWorkflowForApproval",
  "reviewAssistantWorkflow",
] as const;

export type AssistantPlatformQueryOperationId =
  (typeof ASSISTANT_PLATFORM_QUERY_OPERATION_IDS)[number];
export type AssistantPlatformMutationOperationId =
  (typeof ASSISTANT_PLATFORM_MUTATION_OPERATION_IDS)[number];

export type AssistantPlatformQueryArgsMap = {
  listAssistantTools: {
    pathname?: string;
    module?: z.infer<typeof IntellioAssistantModuleSchema>;
    persona?: string;
    limit?: number;
    catalogScope?: "route" | "module" | "staff-global";
  };
  listAssistantCapabilities: {
    pathname?: string;
    module?: z.infer<typeof IntellioAssistantModuleSchema>;
    persona?: string;
    types?: AssistantCapabilityType[];
    limit?: number;
    catalogScope?: "route" | "module" | "staff-global";
  };
  previewAssistantAction: {
    capabilityId: string;
    pathname?: string;
    module?: z.infer<typeof IntellioAssistantModuleSchema>;
    persona?: string;
    context?: AppSyncJsonArgument<Record<string, unknown>>;
  };
  previewAssistantReportGeneration: IntellioAssistantReportPreviewArguments;
  getAssistantReportGenerationWorkspace: IntellioAssistantReportGenerationWorkspaceArguments;
  getAssistantFeedbackAuthority: {
    feedbackId: string;
    feedbackArtifactRef: string;
    replayHash?: string | null;
  };
  getAssistantLearningStatus: {
    assistantEpisodeId: string;
    route: string;
    horizonHours?: number | null;
  };
  getAssistantRuntimePolicy: {
    pathname?: string;
    module?: z.infer<typeof IntellioAssistantModuleSchema>;
    persona?: string;
  };
  listAssistantWorkItems: IntellioAssistantWorkItemsQueryArguments;
  getAssistantSessionState: IntellioAssistantSessionStateArguments;
  listAssistantConversationSessions: IntellioAssistantConversationSessionQueryArguments;
  getAssistantConversationSessionTimeline: IntellioAssistantConversationTimelineArguments;
  getAssistantChartArtifactState: {
    sourceRef: string;
    label?: string | null;
    fileName?: string | null;
    mimeType?: string | null;
    route?: string | null;
  };
  getAssistantVisualization3DArtifactState: {
    sourceRef: string;
    label?: string | null;
    fileName?: string | null;
    mimeType?: string | null;
    route?: string | null;
  };
  listAssistantReportTemplates: IntellioAssistantReportTemplateListArguments;
  listAssistantReportSchedules: IntellioAssistantReportScheduleListArguments;
  listAssistantWorkflowTemplates: IntellioAssistantWorkflowTemplateQueryArguments;
  listAssistantWorkflows: IntellioAssistantWorkflowListArguments;
  getAssistantWorkflow: IntellioAssistantWorkflowGetArguments;
  getAssistantWorkflowStudioState: IntellioAssistantWorkflowStudioStateArguments;
  listAssistantWorkflowExecutions: IntellioAssistantWorkflowExecutionListArguments;
  getAssistantWorkflowExecution: IntellioAssistantWorkflowExecutionGetArguments;
};

export type AssistantPlatformQueryResultMap = {
  listAssistantTools: AssistantToolConnection;
  listAssistantCapabilities: AssistantCapabilityConnection;
  previewAssistantAction: AssistantActionPreviewResponse;
  previewAssistantReportGeneration: IntellioAssistantReportPreviewOutput;
  getAssistantReportGenerationWorkspace: IntellioAssistantReportGenerationWorkspaceOutput;
  getAssistantFeedbackAuthority: AssistantFeedbackAuthority;
  getAssistantLearningStatus: AssistantLearningStatus;
  getAssistantRuntimePolicy: AssistantRuntimePolicy;
  listAssistantWorkItems: IntellioAssistantWorkItemsQueryOutput;
  getAssistantSessionState: IntellioAssistantSessionStateOutput;
  listAssistantConversationSessions: IntellioAssistantConversationSessionConnection;
  getAssistantConversationSessionTimeline: IntellioAssistantConversationTimelineOutput;
  getAssistantChartArtifactState: AssistantChartArtifactState;
  getAssistantVisualization3DArtifactState: AssistantVisualization3DArtifactState;
  listAssistantReportTemplates: IntellioAssistantReportTemplateConnection;
  listAssistantReportSchedules: IntellioAssistantReportScheduleConnection;
  listAssistantWorkflowTemplates: IntellioAssistantWorkflowTemplateConnection;
  listAssistantWorkflows: IntellioAssistantWorkflowConnection;
  getAssistantWorkflow: IntellioAssistantWorkflowMutationResult["workflow"];
  getAssistantWorkflowStudioState: IntellioAssistantWorkflowStudioStateOutput;
  listAssistantWorkflowExecutions: IntellioAssistantWorkflowExecutionConnection;
  getAssistantWorkflowExecution: IntellioAssistantWorkflowExecutionRecord;
};

export const AssistantArtifactWorkspaceRefSchema =
  IntellioAssistantArtifactRefSchema;
export type AssistantArtifactWorkspaceRef = z.infer<
  typeof AssistantArtifactWorkspaceRefSchema
>;

export const AssistantChartSeriesSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    color: z.string(),
  })
  .strict();
export type AssistantChartSeries = z.infer<typeof AssistantChartSeriesSchema>;

export const AssistantChartPanelSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    subtitle: z.string().optional(),
    kind: z.enum(["bar", "line", "area", "stacked-bar"]),
    xKey: z.string(),
    series: z.array(AssistantChartSeriesSchema).min(1),
    data: z.array(z.record(z.string(), z.unknown())).min(1),
  })
  .strict();
export type AssistantChartPanel = z.infer<typeof AssistantChartPanelSchema>;

export const AssistantChartArtifactStateQueryInputSchema = z
  .object({
    sourceRef: z.string().min(1),
    label: z.string().nullable().optional(),
    fileName: z.string().nullable().optional(),
    mimeType: z.string().nullable().optional(),
    route: z.string().nullable().optional(),
  })
  .strict();
export type AssistantChartArtifactStateQueryInput = z.infer<
  typeof AssistantChartArtifactStateQueryInputSchema
>;

export const AssistantChartArtifactStateSchema = z
  .object({
    chartArtifact: AssistantArtifactWorkspaceRefSchema.extend({
      artifactType: z.literal("chart"),
    }),
    chartPanels: z.array(AssistantChartPanelSchema).min(1),
  })
  .strict();
export type AssistantChartArtifactState = z.infer<
  typeof AssistantChartArtifactStateSchema
>;

export const AssistantVisualization3DVectorSchema = z
  .object({
    x: z.number(),
    y: z.number(),
    z: z.number(),
  })
  .strict();
export type AssistantVisualization3DVector = z.infer<
  typeof AssistantVisualization3DVectorSchema
>;

export const AssistantVisualization3DMetricValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);
export type AssistantVisualization3DMetricValue = z.infer<
  typeof AssistantVisualization3DMetricValueSchema
>;

export const AssistantVisualization3DNodeSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    kind: z.string().min(1),
    clusterId: z.string().min(1),
    position: AssistantVisualization3DVectorSchema,
    radius: z.number().positive(),
    color: z.string().min(1),
    opacity: z.number().min(0).max(1).optional(),
    metrics: z
      .record(z.string(), AssistantVisualization3DMetricValueSchema)
      .optional(),
    sourceId: z.string().nullable().optional(),
  })
  .strict();
export type AssistantVisualization3DNode = z.infer<
  typeof AssistantVisualization3DNodeSchema
>;

export const AssistantVisualization3DClusterSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    kind: z.string().min(1),
    color: z.string().min(1),
    center: AssistantVisualization3DVectorSchema,
    radius: z.number().positive(),
    metrics: z
      .record(z.string(), AssistantVisualization3DMetricValueSchema)
      .optional(),
  })
  .strict();
export type AssistantVisualization3DCluster = z.infer<
  typeof AssistantVisualization3DClusterSchema
>;

export const AssistantVisualization3DEdgeSchema = z
  .object({
    id: z.string().min(1),
    source: z.string().min(1),
    target: z.string().min(1),
    kind: z.string().min(1),
    weight: z.number().positive().optional(),
    color: z.string().min(1).optional(),
  })
  .strict();
export type AssistantVisualization3DEdge = z.infer<
  typeof AssistantVisualization3DEdgeSchema
>;

export const AssistantVisualization3DLegendItemSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    color: z.string().min(1),
    kind: z.string().min(1).optional(),
    count: z.number().nonnegative().optional(),
  })
  .strict();
export type AssistantVisualization3DLegendItem = z.infer<
  typeof AssistantVisualization3DLegendItemSchema
>;

export const AssistantVisualization3DSpaceSchema = z
  .object({
    id: z.string().min(1),
    title: z.string().min(1),
    description: z.string().optional(),
    generatedAt: z.string().optional(),
    nodes: z.array(AssistantVisualization3DNodeSchema).min(1),
    clusters: z.array(AssistantVisualization3DClusterSchema).min(1),
    edges: z.array(AssistantVisualization3DEdgeSchema),
    legend: z.array(AssistantVisualization3DLegendItemSchema),
    source: z
      .object({
        sourceId: z.string().min(1),
        route: z.string().min(1),
        generatedAt: z.string().min(1),
        windowLimit: z.number().nonnegative(),
        totalCount: z.number().nonnegative(),
        applicantCount: z.number().nonnegative(),
      })
      .strict(),
  })
  .strict();
export type AssistantVisualization3DSpace = z.infer<
  typeof AssistantVisualization3DSpaceSchema
>;

export const AssistantVisualization3DArtifactSchema = z
  .object({
    version: z.literal(1),
    title: z.string().min(1),
    description: z.string().optional(),
    generatedAt: z.string().optional(),
    sourceQuestion: z.string().optional(),
    panels: z.array(AssistantChartPanelSchema).min(1),
    space: AssistantVisualization3DSpaceSchema,
  })
  .strict();
export type AssistantVisualization3DArtifact = z.infer<
  typeof AssistantVisualization3DArtifactSchema
>;

export const AssistantVisualization3DArtifactStateQueryInputSchema =
  AssistantChartArtifactStateQueryInputSchema;
export type AssistantVisualization3DArtifactStateQueryInput = z.infer<
  typeof AssistantVisualization3DArtifactStateQueryInputSchema
>;

export const AssistantVisualization3DArtifactStateSchema = z
  .object({
    visualization3DArtifact: AssistantArtifactWorkspaceRefSchema.extend({
      artifactType: z.literal("visualization3d"),
    }),
    visualization3DSpace: AssistantVisualization3DSpaceSchema,
    visualization3DPanels: z.array(AssistantChartPanelSchema).min(1),
  })
  .strict();
export type AssistantVisualization3DArtifactState = z.infer<
  typeof AssistantVisualization3DArtifactStateSchema
>;

export type AssistantPlatformMutationArgsMap = {
  askAdmissionsAI: AskAdmissionsAIArguments;
  askSchoolAI: AskSchoolAIArguments;
  executeAssistantAction: {
    capabilityId: string;
    pathname?: string;
    module?: z.infer<typeof IntellioAssistantModuleSchema>;
    persona?: string;
    context?: AppSyncJsonArgument<Record<string, unknown>>;
    confirm?: boolean;
  };
  submitAssistantFeedback: {
    input: AppSyncJsonArgument<AssistantFeedbackInput>;
  };
  updateAssistantRuntimePolicy: {
    input: AppSyncJsonArgument<AssistantRuntimePolicyPatch>;
  };
  mutateAssistantWorkItems: IntellioMutateAssistantWorkItemsArguments;
  mutateAssistantRun: IntellioMutateAssistantRunArguments;
  mutateAssistantReportTemplate: IntellioMutateAssistantReportTemplateArguments;
  mutateAssistantReportSchedule: IntellioMutateAssistantReportScheduleArguments;
  startAssistantConversationSession: IntellioStartAssistantConversationSessionArguments;
  closeAssistantConversationSession: IntellioCloseAssistantConversationSessionArguments;
  appendAssistantConversationMessages: IntellioAppendAssistantConversationMessagesArguments;
  upsertAssistantWorkflowDraft: IntellioUpsertAssistantWorkflowDraftArguments;
  submitAssistantWorkflowForApproval: IntellioSubmitAssistantWorkflowForApprovalArguments;
  reviewAssistantWorkflow: IntellioReviewAssistantWorkflowArguments;
};

export type AssistantPlatformMutationResultMap = {
  askAdmissionsAI: AdmissionsAIResponse;
  askSchoolAI: SchoolAIResponse;
  executeAssistantAction: AssistantActionExecutionResponse;
  submitAssistantFeedback: AssistantFeedbackAck;
  updateAssistantRuntimePolicy: AssistantRuntimePolicyAck;
  mutateAssistantWorkItems: IntellioMutateAssistantWorkItemsOutput;
  mutateAssistantRun: IntellioMutateAssistantRunOutput;
  mutateAssistantReportTemplate: IntellioMutateAssistantReportTemplateOutput;
  mutateAssistantReportSchedule: IntellioMutateAssistantReportScheduleOutput;
  startAssistantConversationSession: IntellioAssistantConversationSessionMutationResult;
  closeAssistantConversationSession: IntellioAssistantConversationSessionMutationResult;
  appendAssistantConversationMessages: IntellioAssistantConversationSessionMutationResult;
  upsertAssistantWorkflowDraft: IntellioAssistantWorkflowMutationResult;
  submitAssistantWorkflowForApproval: IntellioAssistantWorkflowMutationResult;
  reviewAssistantWorkflow: IntellioAssistantWorkflowMutationResult;
};

export const AssistantSpreadsheetBridgeScopeSchema = z.enum([
  "https://www.googleapis.com/auth/drive.file",
  "https://www.googleapis.com/auth/spreadsheets",
]);
export type AssistantSpreadsheetBridgeScope = z.infer<
  typeof AssistantSpreadsheetBridgeScopeSchema
>;

export const AssistantSpreadsheetBridgeResponseSchema = z
  .object({
    ok: z.boolean().nullable().optional(),
    status: z.string().nullable().optional(),
    message: z.string().nullable().optional(),
    authorizationUrl: z.string().nullable().optional(),
    state: z.string().nullable().optional(),
    expiresAt: z.string().nullable().optional(),
    fileId: z.string().nullable().optional(),
    fileUrl: z.string().nullable().optional(),
    exportOperationId: z.string().nullable().optional(),
    targetRoute: z.string().nullable().optional(),
    requiredInputs: z.array(z.string()).nullable().optional(),
    payload: z
      .union([z.string(), z.record(z.string(), z.unknown()), z.null()])
      .optional(),
  })
  .strict();
export type AssistantSpreadsheetBridgeResponse = z.infer<
  typeof AssistantSpreadsheetBridgeResponseSchema
>;

export const ASSISTANT_INTEGRATIONS_QUERY_OPERATION_IDS = [
  "getAssistantSpreadsheetBridgeStatus",
] as const;

export const ASSISTANT_INTEGRATIONS_MUTATION_OPERATION_IDS = [
  "startAssistantSpreadsheetBridgeAuth",
  "completeAssistantSpreadsheetBridgeAuth",
  "exportAssistantSpreadsheetToGoogleDrive",
  "disconnectAssistantSpreadsheetBridge",
] as const;

export type AssistantIntegrationsQueryOperationId =
  (typeof ASSISTANT_INTEGRATIONS_QUERY_OPERATION_IDS)[number];
export type AssistantIntegrationsMutationOperationId =
  (typeof ASSISTANT_INTEGRATIONS_MUTATION_OPERATION_IDS)[number];

export type AssistantIntegrationsQueryArgsMap = {
  getAssistantSpreadsheetBridgeStatus: {
    input?: DomainRouterJsonInput;
  };
};

export type AssistantIntegrationsQueryResultMap = {
  getAssistantSpreadsheetBridgeStatus: AssistantSpreadsheetBridgeResponse;
};

export type AssistantIntegrationsMutationArgsMap = {
  startAssistantSpreadsheetBridgeAuth: {
    input: AppSyncJsonArgument<{
      redirectUri?: string;
      requestedScopes?: AssistantSpreadsheetBridgeScope[];
    }>;
  };
  completeAssistantSpreadsheetBridgeAuth: {
    input: AppSyncJsonArgument<{
      state?: string;
      code?: string;
    }>;
  };
  exportAssistantSpreadsheetToGoogleDrive: {
    input: AppSyncJsonArgument<{
      destinationFolderId?: string;
      destinationFileId?: string;
      fileName?: string;
      workbookArtifactPath?: string;
      artifactDownloadUrl?: string;
      mimeType?: string;
    }>;
  };
  disconnectAssistantSpreadsheetBridge: {
    input?: DomainRouterJsonInput;
  };
};

export type AssistantIntegrationsMutationResultMap = {
  startAssistantSpreadsheetBridgeAuth: AssistantSpreadsheetBridgeResponse;
  completeAssistantSpreadsheetBridgeAuth: AssistantSpreadsheetBridgeResponse;
  exportAssistantSpreadsheetToGoogleDrive: AssistantSpreadsheetBridgeResponse;
  disconnectAssistantSpreadsheetBridge: AssistantSpreadsheetBridgeResponse;
};
